import { css, CSSObject } from '@emotion/react'
import {
  CornerRadius,
  gemVar,
  LineWeight,
  Spacing,
  ThemedColors,
  Typography,
} from '@opallabs/gem'
import { buttonLikeSelectors } from '../ButtonLike'
import { base as baseButton, cssVariables } from '../ButtonLike/styles'
import { type QuasiTabsProps } from './types'

const rootClassName = 'lapidary-quasi-tabs'
export const selectors = {
  root: rootClassName,
  option: `${rootClassName}-option`,
} as const

const ButtonSize = {
  Regular: Spacing.M * 2 - LineWeight.Small * 2,
  Small: Spacing.L - LineWeight.Small * 2,
} as const

const sizes = {
  [`&[data-size=regular]`]: {
    blockSize: Spacing.M * 2,

    [`.${selectors.option}`]: {
      blockSize: ButtonSize.Regular,
      columnGap: Spacing.XS,
      paddingInline: Spacing.S,

      '&[data-isicononly=true]': {
        inlineSize: ButtonSize.Regular,
      },
    },
  },
  '&[data-size=small]': {
    blockSize: Spacing.L,

    [`.${selectors.option}`]: {
      blockSize: ButtonSize.Small,
      paddingInline: Spacing.S - 1,

      '&[data-isicononly=true]': {
        inlineSize: ButtonSize.Small,
      },
    },
  },
} as const satisfies Record<
  `&[data-size=${NonNullable<QuasiTabsProps['size']>}]`,
  CSSObject
>

export const root = css(sizes, {
  backgroundColor: gemVar(ThemedColors.Surface1),
  borderColor: gemVar(ThemedColors.StrokeSecondary2),
  borderRadius: CornerRadius.Small,
  borderStyle: 'solid',
  borderWidth: LineWeight.Small,
  boxSizing: 'border-box',
  contain: 'content',
  display: 'flex',
  inlineSize: 'fit-content',

  [`.${selectors.option}:first-child`]: {
    borderStartStartRadius: CornerRadius.Small,
    borderEndStartRadius: CornerRadius.Small,
  },
  [`.${selectors.option}:last-child`]: {
    borderStartEndRadius: CornerRadius.Small,
    borderEndEndRadius: CornerRadius.Small,
  },
})

export const option = css(baseButton, {
  inlineSize: 'fit-content',
  justifyContent: 'center',

  [cssVariables.textColor]: gemVar(ThemedColors.TextSecondary),
  [cssVariables.hoverBackgroundColor]: gemVar(ThemedColors.Primary2),
  [cssVariables.activeBackgroundColor]: gemVar(ThemedColors.Primary3),

  '&[data-isicononly=true]': {
    paddingInline: 'unset',
  },
  '&:not([aria-disabled=true]):not(:disabled)': {
    cursor: 'pointer',
  },
  '&:not([aria-disabled=true])': {
    '&:active, &[aria-checked=true], &[aria-pressed=true]': {
      // Design doesn't use an official color
      color: '#266ADB',
    },
  },

  [`.${buttonLikeSelectors.label}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    ...Typography.T500,
  },
})
